import React, { useState, useEffect } from "react";
import axios from "axios";
import { Switch } from "antd";
import {
  FaEdit,
  FaUniversity,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
  FaSave,
} from "react-icons/fa";
import { toast } from "react-toastify";

// Update this import path to wherever your SummaryApi.js file actually lives.
import SummaryApi from "../common/index";
const notifyUniversitiesChanged = () =>
  window.dispatchEvent(new Event("universitiesUpdated"));
const UniversityManagement = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // --------------------------------------------------
  // Form State
  // --------------------------------------------------
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);

  // --------------------------------------------------
  // Edit Mode
  // --------------------------------------------------
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // --------------------------------------------------
  // Fetch Universities
  // --------------------------------------------------
  ;



  // --------------------------------------------------
  // Statistics
  // --------------------------------------------------
  const totalUniversities = universities.length;

  const activeUniversities = universities.filter(
    (university) => Number(university.is_active) === 1,
  ).length;

  const inactiveUniversities = universities.filter(
    (university) => Number(university.is_active) !== 1,
  ).length;

  // --------------------------------------------------
  // Check Duplicate University
  // --------------------------------------------------
  const nameExists = (value, ignoreId = null) =>
    universities.some(
      (university) =>
        university.university_id !== ignoreId &&
        university.university_name.trim().toLowerCase() ===
        value.trim().toLowerCase(),
    );

  // --------------------------------------------------
  // Reset Form
  // --------------------------------------------------
  const resetForm = () => {
    setName("");
    setIsActive(true);
    setIsEditMode(false);
    setEditId(null);
  };

  // --------------------------------------------------
  // Create University
  // --------------------------------------------------
  const handleCreate = async (e) => {
    e.preventDefault();

    const trimmedName = name.trim();

    if (!trimmedName) {
      toast.error("Please enter university name");
      return;
    }

    if (nameExists(trimmedName)) {
      toast.error("University already exists");
      return;
    }

    setSubmitting(true);
    try {
      const config = SummaryApi.universitycreate;
      await axios({
        url: config.url,
        method: config.method,
        data: {
          university_name: trimmedName,
        },
      });

      toast.success("University created successfully");
      resetForm();

      notifyUniversitiesChanged();
    } catch (error) {
      toast.error("Failed to create university");
    } finally {
      setSubmitting(false);
    }
  };

  // --------------------------------------------------
  // Start Edit
  // --------------------------------------------------
  const handleEdit = (university) => {
    setIsEditMode(true);
    setEditId(university.university_id);
    setName(university.university_name);
    setIsActive(Number(university.is_active) === 1);

    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // --------------------------------------------------
  // Update University
  // --------------------------------------------------
  const handleUpdate = async (e) => {
    e.preventDefault();

    const trimmedName = name.trim();

    if (!trimmedName) {
      toast.error("Please enter university name");
      return;
    }

    if (!editId) {
      toast.error("Invalid university selected");
      return;
    }

    if (nameExists(trimmedName, editId)) {
      toast.error("University already exists");
      return;
    }

    setSubmitting(true);
    try {
      const config = SummaryApi.universityupdate;
      await axios({
        url: config.url,
        method: config.method,
        data: {
          university_id: editId,
          university_name: trimmedName,
          is_active: isActive ? 1 : 0,
        },
      });

      toast.success("University updated successfully");
      resetForm();

      notifyUniversitiesChanged();
    } catch (error) {
      toast.error("Failed to update university");
    } finally {
      setSubmitting(false);
    }
  };

  // --------------------------------------------------
  // Toggle Active / Inactive From Table
  // --------------------------------------------------
  const handleToggle = async (university, checked) => {
    try {
      const config = SummaryApi.universityupdate;
      await axios({
        url: config.url,
        method: config.method,
        data: {
          university_id: university.university_id,
          university_name: university.university_name,
          is_active: checked ? 1 : 0,
        },
      });

      setUniversities((prev) =>
        prev.map((u) =>
          u.university_id === university.university_id
            ? { ...u, is_active: checked ? 1 : 0 }
            : u,
        ),
      );

      notifyUniversitiesChanged(); // <- add this

      toast.success(
        checked
          ? "University activated successfully"
          : "University deactivated successfully",
      );
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className=" p-3 sm:p-5 lg:p-6">
      <style>{`
      .custom-switch-inactive {
        background-color: #9ca3af !important;
      }
      .custom-switch-inactive:hover {
        background-color: #6b7280 !important;
      }
      .custom-switch-active {
        background-color: #16a34a !important;
      }
      .custom-switch-active:hover {
        background-color: #15803d !important;
      }
    `}</style>
      {/* =====================================================
          PAGE HEADER
      ====================================================== */}
      <div className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Left */}
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm"
              style={{
                background: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
              }}
            >
              <FaUniversity
                className="text-xl"
                style={{
                  color: "#c1272d",
                }}
              />
            </div>

            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-800 sm:text-3xl">
                University Management
              </h1>

              <p className="mt-1 text-sm text-gray-500">
                Create and manage universities in the system.
              </p>
            </div>
          </div>

          {/* Total Badge */}
          <div className="flex w-fit items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 shadow-sm">
            <FaBuilding
              className="text-sm"
              style={{
                color: "#c1272d",
              }}
            />

            <span className="text-sm font-semibold text-gray-700">
              {totalUniversities} Universities
            </span>
          </div>
        </div>
      </div>

      {/* =====================================================
          STATISTICS
      ====================================================== */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Total */}
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Total Universities
              </p>

              <h2 className="mt-2 text-2xl font-bold text-gray-800">
                {totalUniversities}
              </h2>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100">
              <FaUniversity className="text-lg text-gray-600" />
            </div>
          </div>
        </div>

        {/* Active */}
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Active Universities
              </p>

              <h2 className="mt-2 text-2xl font-bold text-green-600">
                {activeUniversities}
              </h2>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50">
              <FaCheckCircle className="text-lg text-green-600" />
            </div>
          </div>
        </div>

        {/* Inactive */}
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Inactive Universities
              </p>

              <h2 className="mt-2 text-2xl font-bold text-red-500">
                {inactiveUniversities}
              </h2>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
              <FaTimesCircle className="text-lg text-red-500" />
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          CREATE / EDIT FORM
      ====================================================== */}
      <div
        className={`mb-6 overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-300 ${isEditMode ? "border-blue-200 ring-2 ring-blue-50" : "border-gray-100"
          }`}
      >
        {/* Form Header */}
        <div
          className={`border-b px-5 py-4 sm:px-6 ${isEditMode
            ? "bg-gradient-to-r from-blue-50/70 to-white"
            : "bg-gradient-to-r from-white to-red-50/40"
            }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl ${isEditMode ? "bg-blue-100" : "bg-red-50"
                  }`}
              >
                {isEditMode ? (
                  <FaEdit className="text-blue-600" />
                ) : (
                  <FaPlus
                    style={{
                      color: "#c1272d",
                    }}
                  />
                )}
              </div>

              <div>
                <h2 className="text-base font-bold text-gray-800">
                  {isEditMode ? "Edit University" : "Create University"}
                </h2>

                <p className="mt-0.5 text-xs text-gray-500">
                  {isEditMode
                    ? "Update university information"
                    : "Add a new university to the system"}
                </p>
              </div>
            </div>

            {/* Editing Indicator */}
            {isEditMode && (
              <div className="hidden rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 sm:block">
                Editing ID: {editId}
              </div>
            )}
          </div>
        </div>

        {/* Form Body */}
        <form
          onSubmit={isEditMode ? handleUpdate : handleCreate}
          className="p-5 sm:p-6"
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* University Name */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                University Name
                <span className="ml-1 text-red-500">*</span>
              </label>

              <div className="relative">
                <FaUniversity className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400" />

                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter university name"
                  className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-11 pr-4 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-50"
                />
              </div>

              <p className="mt-1.5 text-xs text-gray-400">
                Enter the official university name.
              </p>
            </div>

            {/* Status */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Status
              </label>

              <div className="flex min-h-[48px] items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg  ${isActive ? "bg-green-100" : "bg-gray-200"
                      }`}
                  >
                    {isActive ? (
                      <FaCheckCircle className="text-green-600" />
                    ) : (
                      <FaTimesCircle className="text-gray-500" />
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      {isActive ? "Active" : "Inactive"}
                    </p>

                    <p className="text-xs text-gray-400">
                      {isActive
                        ? "University is active"
                        : "University is inactive"}
                    </p>
                  </div>
                </div>

                <Switch
                  checked={isActive}
                  onChange={setIsActive}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  className={isActive ? "custom-switch-active" : "custom-switch-inactive"}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
            {/* Cancel Edit */}
            {isEditMode && (
              <button
                type="button"
                onClick={resetForm}
                disabled={submitting}
                className="rounded-xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-600 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel Edit
              </button>
            )}

            {/* Create / Update */}
            <button
              type="submit"
              disabled={submitting}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0 ${isEditMode ? "bg-blue-600 hover:bg-blue-700" : ""
                }`}
              style={
                !isEditMode
                  ? {
                    background:
                      "linear-gradient(135deg, #e05260 0%, #c1272d 100%)",
                  }
                  : undefined
              }
            >
              {isEditMode ? (
                <>
                  <FaSave className="text-xs" />
                  {submitting ? "Updating..." : "Update University"}
                </>
              ) : (
                <>
                  <FaPlus className="text-xs" />
                  {submitting ? "Creating..." : "Create University"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* =====================================================
          UNIVERSITY LIST
      ====================================================== */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {/* List Header */}
        <div className="flex flex-col gap-2 border-b border-gray-100 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="text-base font-bold text-gray-800">Universities</h2>

            <p className="mt-0.5 text-xs text-gray-500">
              View and manage all universities
            </p>
          </div>

          <div className="rounded-lg bg-gray-50 px-3 py-1.5">
            <span className="text-xs font-semibold text-gray-500">
              {totalUniversities} Records
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-left">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                  S.No
                </th>

                <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                  University Name
                </th>

                <th className="px-5 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">
                  Status
                </th>

                <th className="px-5 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-5 py-14 text-center text-sm text-gray-500">
                    Loading universities...
                  </td>
                </tr>
              ) : universities.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-14 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                        <FaUniversity className="text-xl text-gray-400" />
                      </div>

                      <p className="text-sm font-semibold text-gray-600">
                        No universities found
                      </p>

                      <p className="mt-1 text-xs text-gray-400">
                        Create your first university using the form above.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                universities.map((university, index) => (
                  <tr
                    key={university.university_id}
                    className={`group border-b border-gray-100 transition-colors duration-150 last:border-b-0 hover:bg-red-50/30 ${editId === university.university_id && isEditMode
                      ? "bg-blue-50/40"
                      : ""
                      }`}
                  >
                    {/* S.No */}
                    <td className="px-5 py-4">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-600">
                        {index + 1}
                      </span>
                    </td>

                    {/* University */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                          style={{
                            background: "#fff1f2",
                          }}
                        >
                          <FaUniversity
                            className="text-sm"
                            style={{
                              color: "#c1272d",
                            }}
                          />
                        </div>

                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {university.university_name}
                          </p>


                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4 text-center">
                      <div className="inline-flex items-center gap-3">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${Number(university.is_active) === 1
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-600"
                            }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${Number(university.is_active) === 1
                              ? "bg-green-500"
                              : "bg-red-500"
                              }`}
                          />

                          {Number(university.is_active) === 1
                            ? "Active"
                            : "Inactive"}
                        </span>

                        <Switch
                          size="small"
                          checked={Number(university.is_active) === 1}
                          onChange={(checked) =>
                            handleToggle(university, checked)
                          }
                        />
                      </div>
                    </td>

                    {/* Action */}
                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleEdit(university)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all duration-150 ${editId === university.university_id && isEditMode
                          ? "border-blue-200 bg-blue-100 text-blue-700"
                          : "border-blue-100 bg-blue-50 text-blue-600 hover:border-blue-200 hover:bg-blue-100 hover:text-blue-700"
                          }`}
                      >
                        <FaEdit />

                        {editId === university.university_id && isEditMode
                          ? "Editing"
                          : "Edit"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UniversityManagement;